import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import ConfirmModal from './ConfirmModal'
import { sincronizarEventoGoogleCalendar } from '../services/googleCalendarApi'
import './LeadsSDR.css'

/**
 * Tela Leads SDR – apenas SDR e administrador.
 * Cards: Novo Lead | 1° Contato | 2° Contato | 3° Contato | Agendado
 * Cada card = status na tabela leads. Usuário pode mover leads entre os cards.
 */
const COLUNAS = [
  { id: 'novo_lead', titulo: 'Novo Lead', emoji: '🆕' },
  { id: 'contato_1', titulo: '1° Contato', emoji: '1️⃣' },
  { id: 'contato_2', titulo: '2° Contato', emoji: '2️⃣' },
  { id: 'contato_3', titulo: '3° Contato', emoji: '3️⃣' },
  { id: 'agendado', titulo: 'Agendado', emoji: '📅' }
]

export default function LeadsSDR({ onBack, userRole, userEmpresa, userId }) {
  const [leads, setLeads] = useState([])
  const [loading, setLoading] = useState(false)
  const [draggedLead, setDraggedLead] = useState(null)
  const [dragOverColumn, setDragOverColumn] = useState(null)
  const [messageModal, setMessageModal] = useState({ show: false, title: '', message: '', type: 'success' })
  const [moveModal, setMoveModal] = useState({ show: false, lead: null })
  const [responsaveisMap, setResponsaveisMap] = useState({})
  const [responsaveisList, setResponsaveisList] = useState([])
  const [agendadoModal, setAgendadoModal] = useState({ show: false, lead: null })
  const [agendadoForm, setAgendadoForm] = useState({ data: '', hora: '', usuarios_ids: [], observacao: '' })
  const [agendadoSaving, setAgendadoSaving] = useState(false)
  const [editObservacaoModal, setEditObservacaoModal] = useState({ show: false, lead: null, observacao: '' })
  const [editObservacaoSaving, setEditObservacaoSaving] = useState(false)
  const [confirmDesqualificar, setConfirmDesqualificar] = useState({ show: false, lead: null })
  const [showFiltros, setShowFiltros] = useState(false)
  const [showResponsavelDropdown, setShowResponsavelDropdown] = useState(false)
  const [filtros, setFiltros] = useState({
    responsavel: [], // array de ids: múltipla seleção
    dataInicio: '',
    dataFim: '',
    observacao: ''
  })

  const empresaId = userEmpresa?.id || userEmpresa

  const loadResponsaveis = useCallback(async () => {
    try {
      let query = supabase.from('usuarios_sistema').select('id, nome').eq('status', 'ativo').order('nome')
      // Filtro Responsável: apenas usuários da empresa do usuário logado
      if (empresaId) {
        query = query.eq('empresa_id', empresaId)
      }
      // Se o usuário for SDR, carregar somente responsáveis do tipo PAP
      if (userRole === 'sdr') {
        query = query.eq('role', 'pap')
      }
      const { data, error } = await query
      if (error) throw error
      const list = data || []
      const map = {}
      list.forEach(u => { map[u.id] = u.nome || '—' })
      setResponsaveisMap(map)
      setResponsaveisList(list)
    } catch (e) {
      console.error('Erro ao carregar responsáveis:', e)
    }
  }, [empresaId, userRole])

  // Origens que o perfil SDR pode visualizar na tela Leads SDR
  const ORIGENS_SDR = ['pap', 'painel_dados']

  const loadLeads = useCallback(async () => {
    try {
      setLoading(true)
      let query = supabase.from('leads').select('*').order('created_at', { ascending: false })
      if (userRole !== 'administrador') {
        if (!empresaId) {
          setLeads([])
          setLoading(false)
          return
        }
        query = query.eq('empresa_id', empresaId)
        if (userRole === 'sdr') {
          query = query.in('origem', ORIGENS_SDR)
        }
      }
      const { data, error } = await query
      if (error) throw error
      setLeads(data || [])
    } catch (e) {
      console.error('Erro ao carregar leads:', e)
      showMessage('Erro ao carregar leads', 'error')
    } finally {
      setLoading(false)
    }
  }, [userRole, empresaId])

  useEffect(() => {
    if (userRole && (userRole === 'administrador' || empresaId)) loadLeads()
  }, [userRole, empresaId, loadLeads])

  useEffect(() => {
    if (userRole && (userRole === 'administrador' || empresaId)) loadResponsaveis()
  }, [userRole, empresaId, loadResponsaveis])

  const showMessage = (msg, type = 'success') => {
    setMessageModal({ show: true, title: type === 'success' ? 'Sucesso' : 'Erro', message: msg, type })
    setTimeout(() => setMessageModal({ show: false, title: '', message: '', type: 'success' }), 3000)
  }

  const moveLead = async (lead, novoStatus, observacaoOptional) => {
    if (lead.status === novoStatus) return
    if (novoStatus === 'agendado') {
      setMoveModal({ show: false, lead: null, observacao: '' })
      const hoje = new Date()
      const dataStr = hoje.toISOString().slice(0, 10)
      const horaStr = '09:00'
      setAgendadoForm({ data: dataStr, hora: horaStr, usuarios_ids: [], observacao: '' })
      setAgendadoModal({ show: true, lead })
      return
    }
    try {
      const payload = { status: novoStatus, updated_at: new Date().toISOString() }
      if (observacaoOptional !== undefined) payload.observacoes = observacaoOptional && observacaoOptional.trim() ? observacaoOptional.trim() : null
      const { error } = await supabase
        .from('leads')
        .update(payload)
        .eq('id', lead.id)
      if (error) throw error
      setMoveModal({ show: false, lead: null, observacao: '' })
      showMessage('Lead movido.')
      loadLeads()
    } catch (e) {
      showMessage('Erro ao mover lead: ' + e.message, 'error')
    }
  }

  const closeAgendadoModal = () => {
    setAgendadoModal({ show: false, lead: null })
    setAgendadoForm({ data: '', hora: '', usuarios_ids: [], observacao: '' })
  }

  const toggleAgendadoUsuario = (usuarioId) => {
    setAgendadoForm(f => ({
      ...f,
      usuarios_ids: f.usuarios_ids.includes(usuarioId)
        ? f.usuarios_ids.filter(id => id !== usuarioId)
        : [...f.usuarios_ids, usuarioId]
    }))
  }

  const confirmAgendado = async () => {
    const lead = agendadoModal.lead
    if (!lead || !agendadoForm.data || !agendadoForm.hora) {
      showMessage('Preencha data e horário.', 'error')
      return
    }
    try {
      setAgendadoSaving(true)
      const [year, month, day] = agendadoForm.data.split('-').map(Number)
      const [hour, min] = agendadoForm.hora.split(':').map(Number)
      const dataHora = new Date(year, month - 1, day, hour, min, 0, 0).toISOString()
      const nomeEvento = `Lead: ${lead.nome || 'Sem nome'}`
      const { data: ev, error: errEv } = await supabase
        .from('eventos_calendario')
        .insert([{
          nome_evento: nomeEvento,
          data_hora: dataHora,
          id_responsavel: userId,
          observacao: (agendadoForm.observacao && agendadoForm.observacao.trim()) ? agendadoForm.observacao.trim() : (lead.observacoes ? `Lead - ${lead.observacoes}` : null)
        }])
        .select('id')
        .single()
      if (errEv) throw errEv
      if (agendadoForm.usuarios_ids.length > 0) {
        const rows = agendadoForm.usuarios_ids.map(uid => ({ evento_id: ev.id, usuario_id: uid }))
        const { error: errU } = await supabase.from('eventos_calendario_usuarios').insert(rows)
        if (errU) throw errU
      }
      await sincronizarEventoGoogleCalendar({ usuario_id: userId, evento_id: ev.id, op: 'upsert' })
      const { error: errLead } = await supabase
        .from('leads')
        .update({ status: 'agendado', updated_at: new Date().toISOString() })
        .eq('id', lead.id)
      if (errLead) throw errLead
      showMessage('Evento agendado e lead movido para Agendado.')
      closeAgendadoModal()
      loadLeads()
    } catch (e) {
      showMessage('Erro ao agendar: ' + (e.message || e), 'error')
    } finally {
      setAgendadoSaving(false)
    }
  }

  const openMoveModal = (lead, e) => {
    e.stopPropagation()
    e.preventDefault()
    setMoveModal({ show: true, lead, observacao: lead.observacoes || '' })
  }

  const openEditObservacao = (lead, e) => {
    e.stopPropagation()
    e.preventDefault()
    setEditObservacaoModal({ show: true, lead, observacao: lead.observacoes || '' })
  }

  const saveObservacao = async () => {
    if (!editObservacaoModal.lead) return
    try {
      setEditObservacaoSaving(true)
      const { error } = await supabase
        .from('leads')
        .update({
          observacoes: editObservacaoModal.observacao.trim() || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', editObservacaoModal.lead.id)
      if (error) throw error
      showMessage('Observação salva.')
      setEditObservacaoModal({ show: false, lead: null, observacao: '' })
      loadLeads()
    } catch (e) {
      showMessage('Erro ao salvar: ' + e.message, 'error')
    } finally {
      setEditObservacaoSaving(false)
    }
  }

  const desqualificarLead = async (lead) => {
    if (!lead) return
    try {
      const { error } = await supabase
        .from('leads')
        .update({ status: 'fechado_perdeu', updated_at: new Date().toISOString() })
        .eq('id', lead.id)
      if (error) throw error
      setConfirmDesqualificar({ show: false, lead: null })
      showMessage('Lead desqualificado.')
      loadLeads()
    } catch (e) {
      showMessage('Erro ao desqualificar: ' + e.message, 'error')
    }
  }

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', lead.id)
  }

  const handleDragOver = (e, statusId) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverColumn(statusId)
  }

  const handleDragLeave = () => setDragOverColumn(null)

  const handleDrop = (e, novoStatus) => {
    e.preventDefault()
    setDragOverColumn(null)
    if (!draggedLead || draggedLead.status === novoStatus) {
      setDraggedLead(null)
      return
    }
    if (novoStatus === 'agendado') {
      const lead = draggedLead
      setDraggedLead(null)
      const hoje = new Date()
      setAgendadoForm({ data: hoje.toISOString().slice(0, 10), hora: '09:00', usuarios_ids: [], observacao: '' })
      setAgendadoModal({ show: true, lead })
      return
    }
    moveLead(draggedLead, novoStatus)
    setDraggedLead(null)
  }

  const responsaveisSelecionados = Array.isArray(filtros.responsavel) ? filtros.responsavel : (filtros.responsavel ? [filtros.responsavel] : [])

  const leadsFiltrados = React.useMemo(() => {
    return leads.filter((lead) => {
      if (responsaveisSelecionados.length > 0) {
        if (!lead.responsavel || !responsaveisSelecionados.includes(lead.responsavel)) return false
      }
      const dataLead = lead.data_hora_contato || lead.data_contato
      if (dataLead) {
        const dataStr = typeof dataLead === 'string' ? dataLead.split('T')[0] : new Date(dataLead).toISOString().split('T')[0]
        if (filtros.dataInicio && dataStr < filtros.dataInicio) return false
        if (filtros.dataFim && dataStr > filtros.dataFim) return false
      } else if (filtros.dataInicio || filtros.dataFim) return false
      if (filtros.observacao && filtros.observacao.trim()) {
        const palavras = filtros.observacao.trim().toLowerCase().split(/\s+/).filter(Boolean)
        const texto = (lead.observacoes || '').toLowerCase()
        const todasPresentes = palavras.every((p) => texto.includes(p))
        if (!todasPresentes) return false
      }
      return true
    })
  }, [leads, filtros])

  const getLeadsByStatus = (status) => leadsFiltrados.filter(l => l.status === status)

  const formatCurrency = (v) => {
    if (v == null) return '—'
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v)
  }

  const formatDate = (d) => {
    if (!d) return '—'
    return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
  }
  const formatDataHora = (d) => {
    if (!d) return '—'
    return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div className="leads-sdr-container">
      <header className="leads-sdr-header">
        <button type="button" className="leads-sdr-btn-back" onClick={onBack}>← Voltar</button>
        <h1 className="leads-sdr-title">📞 Leads SDR</h1>
      </header>

      {/* Filtros (ocultos por padrão) */}
      <div className="leads-sdr-filtros-card">
        <div className="leads-sdr-filtros-header">
          <h2 className="leads-sdr-filtros-title">🔍 Filtros</h2>
          <button
            type="button"
            className="leads-sdr-filtros-toggle"
            onClick={() => {
              setShowFiltros((s) => !s)
              if (showFiltros) setShowResponsavelDropdown(false)
            }}
            aria-expanded={showFiltros}
          >
            {showFiltros ? '▲ Ocultar filtros' : '▼ Mostrar filtros'}
          </button>
        </div>
        {showFiltros && (
          <div className="leads-sdr-filtros-body">
            <div className="leads-sdr-filtros-row">
              <div className="leads-sdr-filtros-field leads-sdr-filtros-field-responsavel">
                <label className="leads-sdr-filtros-label">Responsável</label>
                <div className="leads-sdr-filtros-dropdown">
                  <button
                    type="button"
                    className="leads-sdr-filtros-dropdown-btn"
                    onClick={() => setShowResponsavelDropdown((s) => !s)}
                    aria-expanded={showResponsavelDropdown}
                    aria-haspopup="listbox"
                    id="filtro-responsavel"
                  >
                    <span className="leads-sdr-filtros-dropdown-text">
                      {responsaveisSelecionados.length === 0
                        ? 'Todos'
                        : responsaveisSelecionados.length === responsaveisList.length
                          ? 'Todos os responsáveis'
                          : `${responsaveisSelecionados.length} selecionado(s)`}
                    </span>
                    <span className="leads-sdr-filtros-dropdown-arrow">{showResponsavelDropdown ? '▲' : '▼'}</span>
                  </button>
                  <ul
                    className={`leads-sdr-filtros-dropdown-menu ${showResponsavelDropdown ? 'show' : ''}`}
                    role="listbox"
                  >
                    <li className="leads-sdr-filtros-dropdown-item">
                      <label className="leads-sdr-filtros-dropdown-check">
                        <input
                          type="checkbox"
                          checked={responsaveisSelecionados.length === responsaveisList.length}
                          onChange={(e) => setFiltros((f) => ({ ...f, responsavel: e.target.checked ? responsaveisList.map((u) => u.id) : [] }))}
                          onClick={(e) => e.stopPropagation()}
                          className="leads-sdr-filtros-checkbox"
                        />
                        <span className="leads-sdr-filtros-dropdown-item-text">Todos</span>
                      </label>
                    </li>
                    <li className="leads-sdr-filtros-dropdown-divider" />
                    {responsaveisList.map((u) => (
                      <li key={u.id} className="leads-sdr-filtros-dropdown-item">
                        <label className="leads-sdr-filtros-dropdown-check">
                          <input
                            type="checkbox"
                            checked={responsaveisSelecionados.includes(u.id)}
                            onChange={() => {
                              setFiltros((f) => {
                                const arr = Array.isArray(f.responsavel) ? f.responsavel : (f.responsavel ? [f.responsavel] : [])
                                const next = arr.includes(u.id) ? arr.filter((id) => id !== u.id) : [...arr, u.id]
                                return { ...f, responsavel: next }
                              })
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="leads-sdr-filtros-checkbox"
                          />
                          <span className="leads-sdr-filtros-dropdown-item-text">{u.nome || '—'}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
                {showResponsavelDropdown && (
                  <div
                    className="leads-sdr-filtros-dropdown-backdrop"
                    onClick={() => setShowResponsavelDropdown(false)}
                    aria-hidden="true"
                  />
                )}
              </div>
              <div className="leads-sdr-filtros-field">
                <label htmlFor="filtro-data-inicio">Data início</label>
                <input
                  type="date"
                  id="filtro-data-inicio"
                  value={filtros.dataInicio}
                  onChange={(e) => setFiltros((f) => ({ ...f, dataInicio: e.target.value }))}
                  className="leads-sdr-filtros-input"
                />
              </div>
              <div className="leads-sdr-filtros-field">
                <label htmlFor="filtro-data-fim">Data fim</label>
                <input
                  type="date"
                  id="filtro-data-fim"
                  value={filtros.dataFim}
                  onChange={(e) => setFiltros((f) => ({ ...f, dataFim: e.target.value }))}
                  className="leads-sdr-filtros-input"
                />
              </div>
              <div className="leads-sdr-filtros-field leads-sdr-filtros-field-obs">
                <label htmlFor="filtro-observacao">Observação (palavras contidas)</label>
                <input
                  type="text"
                  id="filtro-observacao"
                  value={filtros.observacao}
                  onChange={(e) => setFiltros((f) => ({ ...f, observacao: e.target.value }))}
                  placeholder="Digite palavras que devem aparecer na observação"
                  className="leads-sdr-filtros-input"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {messageModal.show && (
        <ConfirmModal
          show
          title={messageModal.title}
          message={messageModal.message}
          type={messageModal.type}
          showCancelButton={false}
          confirmText="OK"
          onConfirm={() => setMessageModal({ show: false, title: '', message: '', type: 'success' })}
        />
      )}

      {moveModal.show && moveModal.lead && (
        <div className="leads-sdr-modal-overlay" onClick={() => setMoveModal({ show: false, lead: null, observacao: '' })}>
          <div className="leads-sdr-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="leads-sdr-modal-title">Para onde deseja mover este lead?</h3>
            <p className="leads-sdr-modal-lead">{moveModal.lead.nome || '—'}</p>
            <div className="leads-sdr-move-observacao">
              <label htmlFor="move-observacao">Observação (opcional)</label>
              <textarea
                id="move-observacao"
                className="leads-sdr-move-observacao-input"
                placeholder="Adicione ou edite a observação do lead"
                value={moveModal.observacao ?? ''}
                onChange={(e) => setMoveModal(prev => ({ ...prev, observacao: e.target.value }))}
                rows={3}
              />
            </div>
            <div className="leads-sdr-modal-options">
              {COLUNAS.filter(c => c.id !== moveModal.lead.status).map((col) => (
                <button
                  key={col.id}
                  type="button"
                  className="leads-sdr-modal-btn"
                  onClick={() => moveLead(moveModal.lead, col.id, moveModal.observacao)}
                >
                  <span className="leads-sdr-modal-btn-emoji">{col.emoji}</span>
                  {col.titulo}
                </button>
              ))}
            </div>
            <button type="button" className="leads-sdr-modal-cancel" onClick={() => setMoveModal({ show: false, lead: null, observacao: '' })}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {agendadoModal.show && agendadoModal.lead && (
        <div className="leads-sdr-modal-overlay" onClick={closeAgendadoModal}>
          <div className="leads-sdr-modal leads-sdr-modal-agendado" onClick={(e) => e.stopPropagation()}>
            <h3 className="leads-sdr-modal-title">Agendar evento</h3>
            <p className="leads-sdr-modal-lead">Título: <strong>Lead: {agendadoModal.lead.nome || '—'}</strong></p>
            <div className="leads-sdr-agendado-form">
              <div className="leads-sdr-agendado-row">
                <label>Data</label>
                <input
                  type="date"
                  value={agendadoForm.data}
                  onChange={(e) => setAgendadoForm(f => ({ ...f, data: e.target.value }))}
                />
              </div>
              <div className="leads-sdr-agendado-row">
                <label>Horário</label>
                <input
                  type="time"
                  value={agendadoForm.hora}
                  onChange={(e) => setAgendadoForm(f => ({ ...f, hora: e.target.value }))}
                />
              </div>
              <div className="leads-sdr-agendado-row">
                <label>Observação</label>
                <textarea
                  className="leads-sdr-agendado-observacao"
                  placeholder="Opcional"
                  value={agendadoForm.observacao}
                  onChange={(e) => setAgendadoForm(f => ({ ...f, observacao: e.target.value }))}
                  rows={3}
                />
              </div>
              <div className="leads-sdr-agendado-row">
                <label>Usuários associados</label>
                <div className="leads-sdr-agendado-usuarios">
                  {responsaveisList.map((u) => (
                    <label key={u.id} className="leads-sdr-agendado-check">
                      <input
                        type="checkbox"
                        checked={agendadoForm.usuarios_ids.includes(u.id)}
                        onChange={() => toggleAgendadoUsuario(u.id)}
                      />
                      <span>{u.nome || '—'}</span>
                    </label>
                  ))}
                  {responsaveisList.length === 0 && <span className="leads-sdr-agendado-empty">Nenhum usuário carregado</span>}
                </div>
              </div>
            </div>
            <div className="leads-sdr-modal-actions">
              <button type="button" className="leads-sdr-modal-cancel" onClick={closeAgendadoModal}>Cancelar</button>
              <button type="button" className="leads-sdr-agendado-btn-confirm" onClick={confirmAgendado} disabled={agendadoSaving || !agendadoForm.data || !agendadoForm.hora}>
                {agendadoSaving ? 'Agendando...' : 'Agendar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editObservacaoModal.show && editObservacaoModal.lead && (
        <div className="leads-sdr-modal-overlay" onClick={() => setEditObservacaoModal({ show: false, lead: null, observacao: '' })}>
          <div className="leads-sdr-modal leads-sdr-modal-editar-obs" onClick={(e) => e.stopPropagation()}>
            <h3 className="leads-sdr-modal-title">Editar observação</h3>
            <p className="leads-sdr-modal-lead">{editObservacaoModal.lead.nome || '—'}</p>
            <textarea
              className="leads-sdr-move-observacao-input"
              placeholder="Observação do lead"
              value={editObservacaoModal.observacao}
              onChange={(e) => setEditObservacaoModal(prev => ({ ...prev, observacao: e.target.value }))}
              rows={4}
            />
            <div className="leads-sdr-modal-actions">
              <button type="button" className="leads-sdr-modal-cancel" onClick={() => setEditObservacaoModal({ show: false, lead: null, observacao: '' })}>Cancelar</button>
              <button type="button" className="leads-sdr-agendado-btn-confirm" onClick={saveObservacao} disabled={editObservacaoSaving}>{editObservacaoSaving ? 'Salvando...' : 'Salvar'}</button>
            </div>
          </div>
        </div>
      )}

      {confirmDesqualificar.show && confirmDesqualificar.lead && (
        <ConfirmModal
          show
          title="Desqualificar lead"
          message={`Tem certeza que deseja desqualificar o lead "${confirmDesqualificar.lead.nome || '—'}"? O status será alterado para Fechado (Perdido).`}
          type="warning"
          showCancelButton={true}
          confirmText="Sim, desqualificar"
          cancelText="Cancelar"
          onConfirm={() => desqualificarLead(confirmDesqualificar.lead)}
          onCancel={() => setConfirmDesqualificar({ show: false, lead: null })}
        />
      )}

      {loading ? (
        <div className="leads-sdr-loading">Carregando...</div>
      ) : (
        <div className="leads-sdr-kanban">
          {COLUNAS.map((col) => {
            const colLeads = getLeadsByStatus(col.id)
            const isOver = dragOverColumn === col.id
            return (
              <div
                key={col.id}
                className={`leads-sdr-column ${isOver ? 'leads-sdr-column-over' : ''}`}
                onDragOver={(e) => handleDragOver(e, col.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, col.id)}
              >
                <div className="leads-sdr-column-header">
                  <span className="leads-sdr-column-emoji">{col.emoji}</span>
                  <span className="leads-sdr-column-title">{col.titulo}</span>
                  <span className="leads-sdr-column-count">{colLeads.length}</span>
                </div>
                <div className="leads-sdr-column-cards">
                  {colLeads.map((lead) => (
                    <div
                      key={lead.id}
                      className="leads-sdr-card"
                      draggable
                      onDragStart={(e) => handleDragStart(e, lead)}
                      data-lead-id={lead.id}
                    >
                      <div className="leads-sdr-card-nome">{lead.nome || '—'}</div>
                      {lead.telefone && <div className="leads-sdr-card-tel">{lead.telefone}</div>}
                      {lead.valor_interesse != null && (
                        <div className="leads-sdr-card-valor">{formatCurrency(lead.valor_interesse)}</div>
                      )}
                      <div className="leads-sdr-card-data">{lead.data_hora_contato ? formatDataHora(lead.data_hora_contato) : formatDate(lead.data_contato)}</div>
                      {lead.responsavel && (
                        <div className="leads-sdr-card-responsavel">👤 {responsaveisMap[lead.responsavel] || '—'}</div>
                      )}
                      {lead.observacoes && (
                        <div className="leads-sdr-card-observacoes" title={lead.observacoes}>{lead.observacoes}</div>
                      )}
                      <div className="leads-sdr-card-actions">
                        <button
                          type="button"
                          className="leads-sdr-card-btn-editar-obs"
                          onClick={(e) => openEditObservacao(lead, e)}
                          title="Editar observação"
                        >
                          ✏️ Observação
                        </button>
                        <button
                          type="button"
                          className="leads-sdr-card-btn-desqualificar"
                          onClick={(e) => { e.stopPropagation(); e.preventDefault(); setConfirmDesqualificar({ show: true, lead }) }}
                          title="Desqualificar lead"
                        >
                          Desqualificar
                        </button>
                        <button
                          type="button"
                          className="leads-sdr-card-btn-mover"
                          onClick={(e) => openMoveModal(lead, e)}
                          title="Mover para outra coluna"
                        >
                          Mover
                        </button>
                        <span className="leads-sdr-card-hint">ou arraste o card</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
